import React from 'react'
import classNames from 'classnames'
import {
    WemoloCheckbox,
    WemoloClockIcon,
    WemoloEyeOpenIcon,
    WemoloImageIcon,
    WemoloLicensePlate,
    WemoloScannerIcon,
    WemoloTypography
} from '../../../components/Wemolo'
import { CategoryTone, categoryTone, DetectionExplorerKnownCountry, DETECTION_EXPLORER_TEXT } from '../config'
import { CategoryBadge } from './CategoryBadge'
import { DirectionChip, VehicleTypeChip } from './Chips'
import styles from './Gallery.module.css'

// A light tint over the whole card by category tone (see categoryTone) — green correct, amber/red
// errors, grey uncategorized — so each detection's status reads at a glance.
const CARD_TONE_CLASS: Record<CategoryTone, string> = {
    correct: styles.toneCorrect,
    warning: styles.toneWarning,
    critical: styles.toneCritical,
    uncategorized: styles.toneUncategorized
}

export type GalleryItem = {
    id: string
    licensePlate: string
    country: DetectionExplorerKnownCountry
    /** Raw API direction (IN/OUT/INV or the "not_set" sentinel). */
    direction: string
    /** Raw API vehicle type (or the "not_set" sentinel). */
    vehicleType: string
    timestamp: string
    /** Numeric device id surfaced as a scanner badge on the media. */
    deviceId: number
    /** Signed URL for the media tile (render image, falling back to ROI). When present the tile renders it. */
    imageUrl?: string
    /** Signed URL for the cropped license-plate thumbnail (`images.lp.url`). */
    lpImageUrl?: string
    /** Raw API detection category (e.g. "match" or the "not_set" sentinel). Rendered as a chip. */
    category: string
    /** Short letter badges shown in the top-right corner (e.g. ["C", "E"]). */
    cornerBadges?: string[]
}

export type GalleryCardProps = {
    item: GalleryItem
    isSelected: boolean
    onToggleSelect: (id: string) => void
    onOpenDetail: (id: string) => void
}

export const GalleryCard: React.FC<GalleryCardProps> = ({ item, isSelected, onToggleSelect, onOpenDetail }) => (
    <div
        className={classNames(styles.card, CARD_TONE_CLASS[categoryTone(item.category)], {
            [styles.cardSelected]: isSelected
        })}
        onClick={() => onToggleSelect(item.id)}
    >
        <div className={styles.media}>
            {item.imageUrl ? (
                <img
                    src={item.imageUrl}
                    alt={`Detection ${item.licensePlate}`}
                    className={styles.mediaImage}
                    loading="lazy"
                />
            ) : (
                <>
                    <WemoloImageIcon className={styles.mediaIcon} />
                    <WemoloTypography variant="body-small" tag="span" className={styles.mediaLabel}>
                        {DETECTION_EXPLORER_TEXT.onDevice}
                    </WemoloTypography>
                </>
            )}
            <button
                type="button"
                className={styles.viewFullSizeButton}
                title={DETECTION_EXPLORER_TEXT.viewFullSize}
                aria-label={DETECTION_EXPLORER_TEXT.viewFullSize}
                onClick={(e) => {
                    e.stopPropagation()
                    onOpenDetail(item.id)
                }}
            >
                <WemoloEyeOpenIcon className={styles.viewFullSizeIcon} />
            </button>
        </div>

        <div className={styles.topBar}>
            <div
                className={`${styles.checkboxSlot} ${isSelected ? styles.checkboxSlotVisible : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <WemoloCheckbox checked={isSelected} onChange={() => onToggleSelect(item.id)} />
            </div>
            {item.cornerBadges && item.cornerBadges.length > 0 && (
                <div className={styles.cornerBadges}>
                    {item.cornerBadges.map((letter) => (
                        <span key={letter} className={styles.cornerBadge}>
                            {letter}
                        </span>
                    ))}
                </div>
            )}
        </div>

        <div className={styles.footer}>
            <div className={styles.footerCol}>
                <span className={styles.deviceBadge}>
                    <WemoloScannerIcon className={styles.deviceBadgeIcon} />
                    <WemoloTypography variant="label-small" tag="span">
                        {item.deviceId}
                    </WemoloTypography>
                </span>
                <CategoryBadge category={item.category} />
                {item.vehicleType && <VehicleTypeChip vehicleType={item.vehicleType} />}
                <DirectionChip direction={item.direction} />
            </div>
            <div className={`${styles.footerCol} ${styles.footerColRight}`}>
                <div className={styles.plateScale}>
                    {item.lpImageUrl && (
                        <img
                            src={item.lpImageUrl}
                            alt={`License plate ${item.licensePlate}`}
                            className={styles.lpImage}
                            loading="lazy"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                    )}
                    {item.licensePlate && (
                        <WemoloLicensePlate plateNumber={item.licensePlate} size="small" country={item.country} />
                    )}
                </div>
                <span className={styles.timestamp}>
                    <WemoloClockIcon className={styles.timestampIcon} />
                    <WemoloTypography variant="label-small" tag="span">
                        {item.timestamp}
                    </WemoloTypography>
                </span>
            </div>
        </div>
    </div>
)
